import { Observable, fromPromise } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

import { getGlimpseApiToken } from '../server/store'
import {
  getValidGlimpseToken,
  forceRefreshGlimpseToken,
  isReauthRequired,
} from '../redux/services/auth'
import { isLogoutInFlight } from '../redux/thunks/logoutState'

const FORCED_REFRESH_WINDOW_MS = 60 * 1000
const FORCED_REFRESH_LIMIT = 2

/**
 * Circuit breaker: fed only by forced refreshes triggered BY an auth error.
 * Cuts off the persistent case — a refresh that "succeeds" but the backend
 * keeps rejecting fresh tokens (clock skew, key rotation, a misconfigured
 * audience) — which would otherwise force a refresh every refetchInterval
 * cycle forever. Non-auth errors never touch it.
 *
 * @type {number[]}
 */
let forcedRefreshTimestamps = []

const pruneForcedRefreshes = now => {
  forcedRefreshTimestamps = forcedRefreshTimestamps.filter(
    timestamp => now - timestamp < FORCED_REFRESH_WINDOW_MS,
  )
}

const isCircuitOpen = () => {
  pruneForcedRefreshes(Date.now())
  return forcedRefreshTimestamps.length >= FORCED_REFRESH_LIMIT
}

const registerForcedRefresh = () => {
  const now = Date.now()
  pruneForcedRefreshes(now)
  forcedRefreshTimestamps.push(now)
}

export const isAuthError = graphQLErrors =>
  Boolean(graphQLErrors) &&
  graphQLErrors.some(
    e =>
      e.extensions?.code === 'UNAUTHENTICATED' || (e.message && e.message.includes('JWT INVALID')),
  )

/**
 * Sets the credential and stamps the token used onto the operation's context,
 * so the error link knows exactly which credential the backend rejected.
 *
 * @param {object} deps
 * @param {() => object} deps.getAuth0 - Returns the current MSAL adapter instance.
 * @returns {import('@apollo/client').ApolloLink}
 */
export const createAuthLink = ({ getAuth0 }) =>
  setContext(async (_, { headers }) => {
    const token = await getValidGlimpseToken(getAuth0())
    return {
      glimpseAuthToken: token,
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    }
  })

const RETRY = 'retry'
const LOGOUT = 'logout'
const PROPAGATE = 'propagate'

/**
 * Decides how to react to an auth error for one failed operation.
 *
 * @param {object} params
 * @param {() => object} params.getAuth0 - Returns the current MSAL adapter instance.
 * @param {string} params.tokenUsed - The credential the backend rejected for this operation.
 * @returns {Promise<'retry'|'logout'|'propagate'>}
 *   - PROPAGATE: the session already needs interactive reauthentication — a
 *     forced refresh can't help, so the original error is surfaced and the
 *     session is kept; the reconnection banner drives recovery. Also covers
 *     a transient refresh failure (e.g. network): React Query will retry on
 *     its own cycle.
 *   - RETRY: a token now exists that differs from the one this operation
 *     used (an absent token compares as `''`, so two absent tokens don't
 *     count as a difference) — typically because another path already
 *     refreshed it, or because a forced refresh just produced a new one.
 *   - LOGOUT: the circuit breaker is open, the refresh can't recover the
 *     session silently (returned null), or the backend still rejects the
 *     freshest token available (refresh returned the same one that failed).
 */
const decideAuthAction = async ({ getAuth0, tokenUsed }) => {
  if (isReauthRequired()) return PROPAGATE
  if (getGlimpseApiToken() !== (tokenUsed || '')) return RETRY
  if (isCircuitOpen()) return LOGOUT

  let refreshed
  try {
    refreshed = await forceRefreshGlimpseToken(getAuth0())
  } catch {
    return PROPAGATE
  }

  if (!refreshed || refreshed === tokenUsed) return LOGOUT

  registerForcedRefresh()
  return RETRY
}

/**
 * On an auth error: refreshes and retries once, or logs out.
 *
 * GraphQL errors from the retry do NOT come back through this handler
 * (@apollo/client 3.x's onError semantics): they reach the caller as an
 * ApolloError, visible to React Query and Sentry. That's what keeps this
 * design free of retry loops.
 *
 * Skips entirely while a logout is already in flight, so it never resurrects
 * a token or fires a second logout mid-cleanup.
 *
 * The logout and propagate branches re-emit the original result. Completing
 * without emitting would resolve client.query with undefined data and
 * resurrect the exact TypeError this design eliminates; emitting nothing at
 * all would leave the query stuck in a fetching state.
 *
 * @param {object} deps
 * @param {() => object} deps.getAuth0 - Returns the current MSAL adapter instance.
 * @param {() => void} deps.onLogout - Dispatches the logout thunk.
 * @returns {import('@apollo/client').ApolloLink}
 */
export const createAuthErrorLink = ({ getAuth0, onLogout }) =>
  onError(({ graphQLErrors, networkError, operation, forward, response }) => {
    if (networkError) return undefined
    if (!isAuthError(graphQLErrors)) return undefined
    if (isLogoutInFlight()) return undefined

    const tokenUsed = operation.getContext().glimpseAuthToken

    return fromPromise(decideAuthAction({ getAuth0, tokenUsed })).flatMap(action => {
      if (action === RETRY) return forward(operation)
      if (action === LOGOUT) onLogout()
      return Observable.of(response)
    })
  })
